<template> 
  <div>
    <b-container v-if="loadingStatus === 'loading'">
      <div class="alert alert-info text-center">
        <h4 style="margin:1rem;">Chargement du questionnaire en cours...</h4>
      </div>
    </b-container>

    <b-container v-if="loadingStatus === 'error' || errormsg">
      <div class="alert alert-danger text-center">
        <span v-if="errormsg" style="color:#d44" v-html="errormsg"></span>
      </div>
    </b-container>

    <b-container v-if="showErrorsOnly">
      <div class="alert alert-info text-center" style="font-size:1.2rem;">
        <strong>Mode "visualisation des erreurs"</strong>, seules les questions non remplies sont visibles.<br/>
        Pour revenir au mode normal,
        <a href="#" class="btn-sm btn-outline-primary" @click="showErrorsOnly = !showErrorsOnly">cliquez ici</a>
      </div>
    </b-container>

    <b-container v-if="test && loadingStatus === 'loaded'">
      <div v-if="viewOnly" class="alert alert-secondary text-center">
        <h4 style="margin:1rem;">Lecture seule activée. Les réponses ne peuvent pas être modifiées.</h4>
      </div>

      <h2 style="color:#501450; font-weight:800;">{{ test.name }}</h2>

      <template v-if="test.aleatoire">
        <b-row v-if="test.scoresNames && test.scoresNames.length" style="margin-top:1rem;">
          <b-container fluid>
            <b-row>
              {{ answeredQuestions }} réponses données sur {{ totalQuestions }} questions.
            </b-row>
            <b-row>
              <table class="table table-hover table-sticky">
                <thead>
                  <tr style="text-align:center;">
                    <th class="header" style="font-weight:400; text-align:left;">
                      {{ answeredQuestions }} / {{ totalQuestions }}
                    </th>
                    <th v-for="(score, scoreIndex) in test.scoresNames" :key="scoreIndex" width="11%">
                      {{ score }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="question in randomizedQuestions" :key="question.id" v-show="!showErrorsOnly || answerHasIssue(question.itemIndex, question.questionIndex)">
                    <td class="header">{{ question.content }}</td>
                    <td v-for="(score, scoreIndex) in test.scoresNames" :key="scoreIndex" class="boxradio">
                      <label style="width:100%" class="labelradio">
                        <input type="radio" :value="scoreIndex" v-model="answers[question.itemIndex + '-' + question.questionIndex]" :disabled="viewOnly" style="margin:auto; display:block;" />
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </b-row>
          </b-container>
        </b-row>

        <b-row v-else-if="hasChoiceQuestions" style="margin-top:1rem;">
          <b-container fluid>
            <b-row>
              {{ answeredQuestions }} réponses données sur {{ totalQuestions }} questions.
            </b-row>
            <b-row>
              <table class="table table-hover table-sticky">
                <thead>
                  <tr style="text-align:center;">
                    <th class="header" style="font-weight:400; text-align:left;">
                      {{ answeredQuestions }} / {{ totalQuestions }}
                    </th>
                    <th v-for="(choice, choiceIndex) in getAllChoices()" :key="choiceIndex" width="11%">
                      {{ choice }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="question in randomizedQuestions" :key="question.id" v-show="!showErrorsOnly || answerHasIssue(question.itemIndex, question.questionIndex)">
                    <td class="header">{{ question.content }}</td>
                    <td v-for="(choice, choiceIndex) in question.choices" :key="choiceIndex" class="boxradio">
                      <label style="width:100%" class="labelradio">
                        <input type="radio" :value="choiceIndex" v-model="answers[question.itemIndex + '-' + question.questionIndex]" :disabled="viewOnly" style="margin:auto; display:block;" />
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </b-row>
          </b-container>
        </b-row>

        <b-row v-else class="justify-content-md-center">
          <b-col cols="6">
            <form>
              <fieldset class="form-group" v-for="question in randomizedQuestions" :key="question.id" v-show="!showErrorsOnly || answerHasIssue(question.itemIndex, question.questionIndex)">
                <legend style="font-size:1rem;">{{ question.content }}</legend>
                <div role="group">
                  <input class="form-control" v-model="answers[question.itemIndex + '-' + question.questionIndex]" :disabled="viewOnly" />
                </div>
              </fieldset>
            </form>
          </b-col>
        </b-row>
      </template>

      <template v-else>
        <b-container v-for="(item, itemIndex) in test.items" :key="itemIndex" style="margin-bottom:1rem;">
          <strong>{{ item.name }}</strong>
          <p v-if="item.description" class="text-muted">{{ item.description }}</p>

          <b-row v-if="item.questions && item.questions.length && test.scoresNames && test.scoresNames.length" style="margin-top:1rem;">
            <b-container fluid>
              <b-row>
                {{ answeredCount(item, itemIndex) }} réponses données sur {{ item.questions.length }} questions.
              </b-row>
              <b-row>
                <table class="table table-hover table-sticky">
                  <thead>
                    <tr style="text-align:center;">
                      <th class="header" style="font-weight:400; text-align:left;">
                        {{ answeredCount(item, itemIndex) }} / {{ item.questions.length }}
                      </th>
                      <th v-for="(score, scoreIndex) in test.scoresNames" :key="scoreIndex" width="11%">
                        {{ score }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(question, questionIndex) in item.questions" :key="questionIndex" v-show="!showErrorsOnly || answerHasIssue(itemIndex, questionIndex)">
                      <td class="header">{{ question.content }}</td>
                      <td v-for="(score, scoreIndex) in test.scoresNames" :key="scoreIndex" class="boxradio">
                        <label style="width:100%" class="labelradio">
                          <input type="radio" :value="scoreIndex" v-model="answers[itemIndex + '-' + questionIndex]" :disabled="viewOnly" style="margin:auto; display:block;" />
                        </label>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </b-row>
            </b-container>
          </b-row>

          <b-row v-else-if="item.questions && item.questions.length && item.questions[0].choices" style="margin-top:1rem;">
            <b-container fluid>
              <b-row>
                {{ answeredCount(item, itemIndex) }} réponses données sur {{ item.questions.length }} questions.
              </b-row>
              <b-row>
                <table class="table table-hover table-sticky">
                  <thead>
                    <tr style="text-align:center;">
                      <th class="header" style="font-weight:400; text-align:left;">
                        {{ answeredCount(item, itemIndex) }} / {{ item.questions.length }}
                      </th>
                      <th v-for="(choice, choiceIndex) in item.questions[0].choices" :key="choiceIndex" width="11%">
                        {{ choice }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(question, questionIndex) in item.questions" :key="questionIndex" v-show="!showErrorsOnly || answerHasIssue(itemIndex, questionIndex)">
                      <td class="header">{{ question.content }}</td>
                      <td v-for="(choice, choiceIndex) in question.choices" :key="choiceIndex" class="boxradio">
                        <label style="width:100%" class="labelradio">
                          <input type="radio" :value="choiceIndex" v-model="answers[itemIndex + '-' + questionIndex]" :disabled="viewOnly" style="margin:auto; display:block;" />
                        </label>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </b-row>
            </b-container>
          </b-row>

          <b-row v-else-if="item.questions && item.questions.length" class="justify-content-md-center">
            <b-col cols="6">
              <form>
                <fieldset class="form-group" v-for="(question, questionIndex) in item.questions" :key="questionIndex" v-show="!showErrorsOnly || answerHasIssue(itemIndex, questionIndex)">
                <legend style="font-size:1rem;">{{ question.content }}</legend>
                <div role="group">
                  <input class="form-control" v-model="answers[itemIndex + '-' + questionIndex]" :disabled="viewOnly" />
                </div>
              </fieldset>
            </form>
          </b-col>
        </b-row>
      </b-container>
    </template>

    <div style="margin-bottom:15rem;"></div>
  </b-container>

  <b-container fluid style="width:100%; position:fixed; bottom:0; background:rgb(244,225,250) !important;">
    <div v-if="!viewOnly" class="d-flex justify-content-end" style="color:#777; width:100%">
      <div id="buttons" style="padding:1rem 0;">
        <div v-if="errormsg && errormsg.length" style="color:#d44"></div>
        <button class="btn btn-primary" @click="saveProgress">Sauvegarder le test</button>
        <button class="btn btn-success" @click="submitAnswers" :disabled="!isFormValid">Terminer le test</button>
        <button v-if="triedValidating && !testIsReady" class="btn btn-warning" @click="showErrorsOnly = !showErrorsOnly">
          {{ showErrorsOnly ? "Voir toutes les réponses" : "Voir les réponses manquantes" }}
        </button>
      </div>
    </div>
  </b-container>
</div>
</template>

<script>
import axios from "axios";
axios.defaults.baseURL = process.env.BASE_URL;

export default {
  name: "NormatifQuestionnaire",
  data() {
    return {
      loadingStatus: "loading",
      test: null,
      answers: {},
      errormsg: null,
      testId: null,
      viewOnly: false,
      showErrorsOnly: false,
      triedValidating: false,
      saved: false,
      qId: null,
      randomizedQuestions: []
    };
  },
  computed: {
    totalQuestions() {
      if (!this.test || !this.test.items) return 0;
      return this.test.items.reduce((total, item) => total + (item.questions ? item.questions.length : 0), 0);
    },
    answeredQuestions() {
      return Object.keys(this.answers).filter(key => this.answers[key] !== null && this.answers[key] !== undefined && this.answers[key] !== "").length;
    },
    progressPercentage() {
      if (this.totalQuestions === 0) return 0;
      return Math.round((this.answeredQuestions / this.totalQuestions) * 100);
    },
    isFormValid() {
      return this.answeredQuestions === this.totalQuestions;
    },
    testIsReady() {
      return this.isFormValid;
    },
    hasChoiceQuestions() {
      if (!this.test || !this.test.items) return false;
      return this.test.items.some(item => item.questions && item.questions[0] && item.questions[0].choices);
    }
  },
  methods: {
    getAllChoices() {
      if (!this.test || !this.test.items) return [];
      for (const item of this.test.items) {
        if (item.questions && item.questions[0] && item.questions[0].choices) {
          return item.questions[0].choices;
        }
      }
      return [];
    },
    randomizeQuestions() {
      if (!this.test || !this.test.items) return [];
      const allQuestions = [];
      this.test.items.forEach((item, itemIndex) => {
        if (item.questions) {
          item.questions.forEach((question, questionIndex) => {
            allQuestions.push({
              ...question,
              itemIndex,
              questionIndex,
              id: `${itemIndex}-${questionIndex}`
            });
          });
        }
      });
      for (let i = allQuestions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [allQuestions[i], allQuestions[j]] = [allQuestions[j], allQuestions[i]];
      }
      return allQuestions;
    },
    async loadTest() {
      try {
        this.loadingStatus = "loading";
        this.testId = this.$route.params.testId || this.$route.query.testId;
        const urlQId = this.$route.query.qId;
        const urlViewOnly = this.$route.query.viewOnly === "true";

        if (!this.testId) throw new Error("ID du test manquant");
        const response = await fetch(`/api/tests/${this.testId}`);
        if (!response.ok) throw new Error("Test non trouvé");
        this.test = await response.json();
        this.loadingStatus = "loaded";

        if (this.test.aleatoire) {
          this.randomizedQuestions = this.randomizeQuestions();
        }

        if (urlQId) {
          this.qId = urlQId;
          this.viewOnly = urlViewOnly;
          await this.loadSavedAnswersFromServer();

        } else {
          const resp = await axios.post("/api/normatif-questionnaire", { test_id: this.testId });
          this.qId = resp.data.id;
          await this.loadSavedAnswersFromServer();
        }
      } catch (error) {
        console.error(error);
        this.errormsg = "Erreur lors du chargement du test: " + error.message;
        this.loadingStatus = "error";
      }
    },
async loadSavedAnswersFromServer() {
  if (!this.qId) return;

  try {
    const response = await axios.get(`/api/normatif-tests/answers/${this.qId}`);

    const data = response.data?.answers ? response.data : response.data.data ? response.data.data : {};
          console.log("response.data =", response.data);

    this.answers = data.answers || {};
    this.viewOnly = !!data.finished;
    this.saved = this.viewOnly;
  } catch (error) {
    console.error("Erreur chargement réponses serveur:", error);
    this.errormsg = "Impossible de charger les réponses enregistrées.";
  }
}

,
    loadSavedAnswers() {
      const saved = localStorage.getItem(`normatif_answers_${this.testId}`);
      if (saved) this.answers = JSON.parse(saved);
    },
    saveProgress() {
      if (this.viewOnly) return;
      localStorage.setItem(`normatif_answers_${this.testId}`, JSON.stringify(this.answers));
      if (this.qId) {
        axios.post("/api/normatif-questionnaire/save", { id: this.qId, answers: this.answers, finished: false }).catch(console.error);
      }
      this.$toast.success("Progrès sauvegardé !");
    },
    async submitAnswers() {
  if (this.viewOnly) return;
  this.triedValidating = true;

  if (!this.isFormValid) {
    this.errormsg = "Merci de répondre à toutes les questions";
    this.$toast.warning(this.errormsg);
    return;
  }

  try {
    localStorage.setItem(`normatif_answers_${this.testId}`, JSON.stringify(this.answers));
    if (this.qId) {
      await axios.post("/api/normatif-questionnaire/save", {
        id: this.qId,
        answers: this.answers,
        finished: false
      });
    }

    const response = await axios.post("/api/normatif-questionnaire/save", {
      id: this.qId,
      answers: this.answers,
      finished: true
    });

    if (response.data.status === "ok") {
      this.saved = true;
      this.viewOnly = true;
      this.$toast.success("Le questionnaire a été envoyé. Lecture seule activée.");
    } else throw new Error("Réponse inattendue du serveur");
  } catch (error) {
    console.error(error);
    this.$toast.error("Erreur envoi: " + (error.response?.data?.message || error.message));
  }
}
,
    answerHasIssue(itemIndex, questionIndex) {
      const answer = this.answers[`${itemIndex}-${questionIndex}`];
      return answer === undefined || answer === null || answer === "";
    },
    itemHasIssue(item, itemIndex) {
      return item.questions && item.questions.some((q, qi) => this.answerHasIssue(itemIndex, qi));
    },
    answeredCount(item, itemIndex) {
      if (!item.questions) return 0;
      return item.questions.filter((q, qi) => this.answers[`${itemIndex}-${qi}`] !== undefined && this.answers[`${itemIndex}-${qi}`] !== null && this.answers[`${itemIndex}-${qi}`] !== "").length;
    },
  },
  async mounted() {
    await this.loadTest();
  }
};
</script>

<style>
.header { font-weight: 800; }
.table-sticky { overflow-y:auto; height:100px; }
.table-sticky thead th { position:sticky; top:0; background-color:#fff; }
th { background: rgb(244,225,250) !important; }
legend { font-weight:600; }
div.alert.alert-success { background: rgb(244,225,250) !important; color:#333; }
#buttons button { margin:auto 0.5rem; }
.boxradio{ vertical-align: middle; }
.boxradio:hover{ background:#fafafa; }

input[type="radio"] {
  accent-color:#501450;
  width:20px;
  height:20px;
}

</style>